import { retryWithAuth } from "./utils/retryWithAuth";

import { MijnOrganisatieListItem } from "../models/user/mijnOrganisatieListItem";
import { getDefaultRoleFromLegacyFlag, OrganisatieUserRole } from "../models/user/userRole";

import { UserManagementWebService } from "../webservices/user-management.web.service";
import { ListParams } from "../models/listParams";
import { LOCAL_STORAGE_KEYS, ORGANISATIE_SWITCH_RESET_KEYS } from "./utils/localStorageKeys";
import { loadJsonFromLocalStorage, removeLocalStorageItem, removeLocalStorageItems, saveJsonToLocalStorage } from "./utils/localStorageState";

export class UserManagementService {

  private normalizeOrganisatie(organisatie: any): MijnOrganisatieListItem {
    if (!organisatie) {
      return organisatie;
    }

    return {
      ...organisatie,
      userType: organisatie.userType ?? getDefaultRoleFromLegacyFlag(false),
    };
  }
  
  // Private static instance of the class (singleton)
  private static instance: UserManagementService;
  // Cache for mijnOrganisaties
  private mijnOrganisatiesCache: any | null = null;
  private account: any;

  // Private constructor to prevent direct instantiation
  private constructor() {
    // this.setAccount();
  }

  // Method to return the singleton instance
  public static getInstance(): UserManagementService {
    if (!UserManagementService.instance) {
      UserManagementService.instance = new UserManagementService();
    }
    return UserManagementService.instance;
  }

  // TODO: Gives error... MSAL config is not initialized. Call initializeMsalConfig first.
  protected async setAccount(): Promise<any> {
    const { authService } = await import("./auth.service"); // Dynamic import
    if (authService) {
      this.account = await authService.getAccount();
    }
  }

  public getStoredOrganisatiePerAccount(): MijnOrganisatieListItem | null {
    const parsedOrgSettings = loadJsonFromLocalStorage<Record<string, MijnOrganisatieListItem>>(
      LOCAL_STORAGE_KEYS.CURRENT_ORGANISATION,
      {},
    );

    if (!this.account) {
      return null;
    }

    if (parsedOrgSettings[this.account.username]) {
      const organisatie = parsedOrgSettings[this.account.username] as MijnOrganisatieListItem;
      return this.normalizeOrganisatie(organisatie);
    }

    // const jsonString = localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_ORGANISATION); // Ophalen van localStorage
    // if (jsonString) {
    //   const organisatie = JSON.parse(jsonString); // Omzetten naar JSON object
    //   return organisatie;
    // }
    
    return null;
  }

  public getStoredOrganisatie(): MijnOrganisatieListItem | null {
    const organisatie = loadJsonFromLocalStorage<MijnOrganisatieListItem | null>(
      LOCAL_STORAGE_KEYS.CURRENT_ORGANISATION,
      null,
    );
    if (organisatie) {
      return this.normalizeOrganisatie(organisatie);
    }
    
    return null;
  }

  public setStoredOrganisatie(organisatie: MijnOrganisatieListItem | null | undefined): void {
    if (!organisatie) {
      console.error('Organisatie is null');
      return;
    }

    try {
        // Bij wisselen van organisatie de opgeslagen lijstfilters resetten
        const previous = this.getStoredOrganisatie();
        if (previous && previous.id !== organisatie.id) {
          this.clearStoredListFilters();
        }

        // Set current organisatie
      saveJsonToLocalStorage(LOCAL_STORAGE_KEYS.CURRENT_ORGANISATION, this.normalizeOrganisatie(organisatie));
    }
    catch (error) {
        // Handle error (e.g., show an error message)
        console.error('Error organisatie simuleren:', error);
    }
  }

  public clearStoredListFilters(): void {
    removeLocalStorageItems(ORGANISATIE_SWITCH_RESET_KEYS);
  }

  public removeStoredOrganisatie(): void {
    try {
        removeLocalStorageItem(LOCAL_STORAGE_KEYS.CURRENT_ORGANISATION);
    }
    catch (error) {
        // Handle error (e.g., show an error message)
        console.error('Error organisatie verwijderen:', error);
    }
  }

  public async getUsersOrganisatie(organisatieId: string, params: ListParams): Promise<any> {
    const userManagementWebService = new UserManagementWebService();

    return retryWithAuth(() => userManagementWebService.getUsersOrganisatie(organisatieId, params), 2);
  }

  public async inviteUserForOrganisatie(organisatieId: string, email: string, role: OrganisatieUserRole): Promise<any> {
    const userManagementWebService = new UserManagementWebService();

    return retryWithAuth(() => userManagementWebService.inviteUserForOrganisatie(organisatieId, email, role), 2);
  }

  public async deleteUserFromOrganisatie(organisatieId: string, email: string, role: OrganisatieUserRole): Promise<any> {
    const userManagementWebService = new UserManagementWebService();

    return retryWithAuth(() => userManagementWebService.deleteUserFromOrganisatie(organisatieId, email, role), 2);
  }

  public async updateUserRoleForOrganisatie(
    organisatieId: string,
    email: string,
    currentRole: OrganisatieUserRole,
    nextRole: OrganisatieUserRole,
  ): Promise<any> {
    if (currentRole === nextRole) {
      return { ok: true };
    }

    await this.deleteUserFromOrganisatie(organisatieId, email, currentRole);
    return this.inviteUserForOrganisatie(organisatieId, email, nextRole);
  }

  public async mijnOrganisaties(forceRefresh: boolean = false): Promise<any> {
    // If cache exists and no refresh is needed, return the cached result
    if (this.mijnOrganisatiesCache && !forceRefresh) {
      return this.mijnOrganisatiesCache;
    }

    const userManagementWebService = new UserManagementWebService();

    // Fetch the data from the API and store it in the cache
    const result = await retryWithAuth(() => userManagementWebService.mijnOrganisaties(), 2);
    this.mijnOrganisatiesCache = result;
    return result;
  }
};

// Export an instance of the userManagementService class
export const userManagementService = UserManagementService.getInstance();